/**
 * Portal rows transcribed from repo-root `ONBOARDING GUIDE - State Logins.xlsx`
 * (State, URL columns and sheet order). When the workbook changes, update this module.
 */
export type StateLoginPortalRow = {
  /** Stable id stored on `StateLoginRow.stateCode` (dispatch / React key). */
  rowId: string
  /** Two-letter code for matching `miscHandledStates` and licensee defaults; null for platform rows. */
  licenseJurisdictionCode: string | null
  /** State column label (matches workbook “State” cell). */
  stateColumnLabel: string
  portalUrl: string
  /** From workbook column B, “Login Needed to Handle Zero Reports”. */
  loginNeededToHandleZeroReports: boolean
  /**
   * From workbook “Separate MISC Account Needed?” (Yes/No). Drives post–SL Logins guided setup.
   * Rows not yet confirmed in the sheet default to false.
   */
  separateMiscAccountNeeded: boolean
}

export const LOGIN_ROW_OPTINS = 'OPTINS' as const
export const LOGIN_ROW_SLIP_PLUS = 'SLIP+' as const
export const SEPARATE_MISC_SLIP_ROW_IDS = ['CA-SLIP', 'NV', 'NJ', 'UT', 'WA-SLIP'] as const
export const SEPARATE_MISC_GUIDE_ROW_IDS = [
  LOGIN_ROW_OPTINS,
  ...SEPARATE_MISC_SLIP_ROW_IDS,
  'TX-WEBFILE',
  'TX-SMART',
  'OR',
  'NC',
  'NY-EFS',
  'NY-DFS',
  'FL',
  'GA',
  'MS',
  'ME',
  'RI',
  'VT',
] as const

export const STATE_LOGIN_PORTAL_ROWS: readonly StateLoginPortalRow[] = [
  {
    rowId: LOGIN_ROW_OPTINS,
    licenseJurisdictionCode: null,
    stateColumnLabel: 'OPTins',
    portalUrl: 'https://login.optins.org/optins/signin.do',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: LOGIN_ROW_SLIP_PLUS,
    licenseJurisdictionCode: null,
    stateColumnLabel: 'SLIP+',
    portalUrl: 'https://slip.slacal.com/Security/Login',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: 'AZ',
    licenseJurisdictionCode: 'AZ',
    stateColumnLabel: 'Arizona',
    portalUrl: 'https://www.sla-az.org/',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'CA-SLIP',
    licenseJurisdictionCode: 'CA',
    stateColumnLabel: 'California',
    portalUrl: 'https://slip.slacal.com/Security/Login',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: 'CA-PTPS',
    licenseJurisdictionCode: 'CA',
    stateColumnLabel: 'California',
    portalUrl: 'https://interactive.web.insurance.ca.gov/apex_extprd/f?p=ONLINE_TAX:LOGIN_DESKTOP',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: 'FL',
    licenseJurisdictionCode: 'FL',
    stateColumnLabel: 'Florida',
    portalUrl: 'https://fslso.slipplus.com/slip/auth/login',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: 'GA',
    licenseJurisdictionCode: 'GA',
    stateColumnLabel: 'Georgia',
    portalUrl: 'https://slip-gaoci.slasuite.com/Common/login.aspx',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: 'ID',
    licenseJurisdictionCode: 'ID',
    stateColumnLabel: 'Idaho',
    portalUrl: 'https://portal.idahosurplusline.org/signin',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: 'IL',
    licenseJurisdictionCode: 'IL',
    stateColumnLabel: 'Illinois',
    portalUrl: 'https://efile.slai.org/login',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: 'KY',
    licenseJurisdictionCode: 'KY',
    stateColumnLabel: 'Kentucky',
    portalUrl: 'https://sso.kyid.ky.gov/',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'ME',
    licenseJurisdictionCode: 'ME',
    stateColumnLabel: 'Maine',
    portalUrl: 'https://revenue.maine.gov/_/',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: 'MD',
    licenseJurisdictionCode: 'MD',
    stateColumnLabel: 'Maryland',
    portalUrl: 'https://www.apps.insurance.maryland.gov/SurplusLines/',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'MN',
    licenseJurisdictionCode: 'MN',
    stateColumnLabel: 'Minnesota',
    portalUrl: 'https://mnsla.com/Login.aspx',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'MS',
    licenseJurisdictionCode: 'MS',
    stateColumnLabel: 'Mississippi',
    portalUrl: 'https://slip.msla.org/Common/login.aspx',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: 'MO',
    licenseJurisdictionCode: 'MO',
    stateColumnLabel: 'Missouri',
    portalUrl: 'https://mologin.mo.gov/account/login',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'NV',
    licenseJurisdictionCode: 'NV',
    stateColumnLabel: 'Nevada',
    portalUrl: 'https://slip.surpluslines.org/Common/login.aspx',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: true,
  },
  {
    rowId: 'NJ',
    licenseJurisdictionCode: 'NJ',
    stateColumnLabel: 'New Jersey',
    portalUrl: 'https://slip.surpluslines.org/Common/login.aspx',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'NM',
    licenseJurisdictionCode: 'NM',
    stateColumnLabel: 'New Mexico',
    portalUrl: 'https://tap.state.nm.us/tap/_/',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'NY-EFS',
    licenseJurisdictionCode: 'NY',
    stateColumnLabel: 'New York',
    portalUrl: 'https://eefs.elany.org/eefs/Login.xhtml',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'NY-DFS',
    licenseJurisdictionCode: 'NY',
    stateColumnLabel: 'New York',
    portalUrl: 'https://myportal.dfs.ny.gov/c/portal/login?',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'NC',
    licenseJurisdictionCode: 'NC',
    stateColumnLabel: 'North Carolina',
    portalUrl: 'https://slip.ncsla.com/Common/login.aspx',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'ND',
    licenseJurisdictionCode: 'ND',
    stateColumnLabel: 'North Dakota',
    portalUrl: 'https://apps.nd.gov/NDINS/surpluslines/Login',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'OR',
    licenseJurisdictionCode: 'OR',
    stateColumnLabel: 'Oregon',
    portalUrl: 'https://slip.oregonsla.org/PublicPages/Login.aspx',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'PA',
    licenseJurisdictionCode: 'PA',
    stateColumnLabel: 'Pennsylvania',
    portalUrl: 'https://www.pasla.org/efs/login',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'RI',
    licenseJurisdictionCode: 'RI',
    stateColumnLabel: 'Rhode Island',
    portalUrl: 'https://taxportal.ri.gov/rptp/portal/home/',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'SC',
    licenseJurisdictionCode: 'SC',
    stateColumnLabel: 'South Carolina',
    portalUrl: 'https://online.doi.sc.gov/Eng/Members/Login.aspx',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'TX-WEBFILE',
    licenseJurisdictionCode: 'TX',
    stateColumnLabel: 'Texas',
    portalUrl: 'https://security.app.cpa.state.tx.us/',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'TX-SMART',
    licenseJurisdictionCode: 'TX',
    stateColumnLabel: 'Texas',
    portalUrl: 'https://smart.sltx.org/Account/Login',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'UT',
    licenseJurisdictionCode: 'UT',
    stateColumnLabel: 'Utah',
    portalUrl: 'https://slip.surpluslines.org/Common/login.aspx',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'VT',
    licenseJurisdictionCode: 'VT',
    stateColumnLabel: 'Vermont',
    portalUrl: 'https://myvtax.vermont.gov/_/',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'WA-ETAX',
    licenseJurisdictionCode: 'WA',
    stateColumnLabel: 'Washington',
    portalUrl: 'https://fortress.wa.gov/oic/onlineservices/Login.aspx?module=TAX',
    loginNeededToHandleZeroReports: true,
    separateMiscAccountNeeded: false,
  },
  {
    rowId: 'WA-SLIP',
    licenseJurisdictionCode: 'WA',
    stateColumnLabel: 'Washington',
    portalUrl: 'https://slip.surpluslines.org/Common/login.aspx',
    loginNeededToHandleZeroReports: false,
    separateMiscAccountNeeded: false,
  },
]

const portalRowById = new Map(STATE_LOGIN_PORTAL_ROWS.map((r) => [r.rowId, r]))

export function getPortalRowByRowId(rowId: string): StateLoginPortalRow | undefined {
  return portalRowById.get(rowId)
}

/** Workbook “Separate MISC Account Needed?” for a portal row id. */
export function separateMiscAccountNeededForRowId(rowId: string): boolean {
  return getPortalRowByRowId(rowId)?.separateMiscAccountNeeded ?? false
}

/** Workbook column B, “Login Needed to Handle Zero Reports”. */
export function loginNeededToHandleZeroReportsForRowId(rowId: string): boolean {
  return getPortalRowByRowId(rowId)?.loginNeededToHandleZeroReports ?? true
}

export function getSeparateMiscGuideRows<
  T extends { stateCode: string; licenseJurisdictionCode: string | null; pendingLoginWizard: boolean },
>(stateLogins: T[], activeBusinessStates: readonly string[]): T[] {
  const activeUpper = new Set(activeBusinessStates.map((code) => code.toUpperCase()))
  return stateLogins.filter((row) => {
    if (row.pendingLoginWizard) return false
    if (!SEPARATE_MISC_GUIDE_ROW_IDS.includes(row.stateCode as (typeof SEPARATE_MISC_GUIDE_ROW_IDS)[number])) {
      return false
    }
    if (row.stateCode === LOGIN_ROW_OPTINS) return true
    const jurisdiction = row.licenseJurisdictionCode?.toUpperCase()
    return jurisdiction ? activeUpper.has(jurisdiction) : false
  })
}

/** Whether the post-SL Logins separate MISC guide step should appear. */
export function shouldShowSeparateMiscAccountGuide(
  stateLogins: { stateCode: string; licenseJurisdictionCode: string | null; pendingLoginWizard: boolean }[],
  activeBusinessStates: readonly string[],
): boolean {
  if (activeBusinessStates.length === 0) return false
  return getSeparateMiscGuideRows(stateLogins, activeBusinessStates).length > 0
}

/** Unique two-letter jurisdictions on the map (for General Questions + OPTins/SLIP+ coverage). */
export const MAP_UNIQUE_LICENSE_JURISDICTION_CODES: readonly string[] = Array.from(
  new Set(
    STATE_LOGIN_PORTAL_ROWS.map((r) => r.licenseJurisdictionCode).filter(
      (c): c is string => c !== null && c.length > 0,
    ),
  ),
).sort((a, b) => a.localeCompare(b))

/** Show OPTins row when the user selects at least one mapped jurisdiction (workbook scope). */
export const OPTINS_COVERED_STATES: readonly string[] = [...MAP_UNIQUE_LICENSE_JURISDICTION_CODES]

/** Show SLIP+ row under the same rule until the workbook encodes narrower triggers. */
export const SLIP_PLUS_COVERED_STATES: readonly string[] = [...MAP_UNIQUE_LICENSE_JURISDICTION_CODES]
